import { riotApi } from "@features/aws-user/api";
import { PropertyManifest } from '@features/home-manifest/types';
import {Device, Property, PropertyUpdate} from '@features/home/types';
import { ManifestEntry } from '@features/plan-manifest/types';
import { HTTPMethodType } from '@lib/types';
import { U } from "@lib/utils";

export type ContainerVersionInfo = {
  current: string | null;
  latest: string | null;
};

export type ContainerVersions = {
  versions: Record<string, ContainerVersionInfo>;
  updated: number;
};

export const homeApi = riotApi.injectEndpoints({
  endpoints: (builder) => {
    return {
      /** get property details */
      getProperty: builder.query<any, { tenantId: string, propertyId: string }>({
        query: ({ tenantId, propertyId }: { tenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(tenantId)}/properties/${U(propertyId)}`,
          method: HTTPMethodType.GET,
        })
      }),
      /** get property devices */
      getDevices: builder.query<Device[], { tenantId: string, propertyId: string }>({
        query: ({ tenantId, propertyId }: { tenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(tenantId)}/properties/${U(propertyId)}/devices`,
          method: HTTPMethodType.GET,
        })
      }),
      /** get property's manifest */
      getManifestEntries: builder.query<any, { tenantId: string, propertyId: string }>({
        query: ({ tenantId, propertyId }: { tenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(tenantId)}/properties/${U(propertyId)}/manifestentries`,
          method: HTTPMethodType.GET,
        }),
      }),
      /** get a plan manifest */
      getManifest: builder.query<any, { tenantId: string, manifestId: string }>({
        query: ({ tenantId, manifestId }: { tenantId: string, manifestId: string }) => ({
          url: `/tenant/${U(tenantId)}/manifests/${U(manifestId)}/entries`,
          method: HTTPMethodType.GET,
        }),
      }),
      /* get a property manifest */
      getPropertyManifest: builder.query<PropertyManifest, {
        tenantId: string, propertyId: string, includeEntries?: boolean
      }>({
        query: ({ tenantId, propertyId, includeEntries }: { tenantId: string, propertyId: string, includeEntries: boolean }) => ({
          url: `/tenant/${U(tenantId)}/properties/${U(propertyId)}/manifest`,
          method: HTTPMethodType.GET,
          params: {
            include_entries: includeEntries,
          }
        }),
      }),
      getPropertyManifestEntries: builder.query<ManifestEntry[], { tenantId: string, propertyId: string }>({
        query: ({ tenantId, propertyId }: { tenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(tenantId)}/properties/${U(propertyId)}/manifestentries`,
          method: HTTPMethodType.GET,
        }),
      }),
      /** update property with given deltas */
      updateProperty: builder.mutation<Property | null, { userTenantId: string, propertyId: string, body: any }>({
        query: ({ userTenantId, propertyId, body }: { userTenantId: string, propertyId: string, body: PropertyUpdate }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}`,
          method: HTTPMethodType.PATCH,
          body,
        }),
      }),
      /** abandon property - remove property from own tenant */
      abandonProperty: builder.mutation<any, { userTenantId: string, propertyId: string }>({
        query: ({ userTenantId, propertyId }: { userTenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(userTenantId)}/tenants/${U(userTenantId)}`,
          method: HTTPMethodType.PATCH,
          body: {
            property_ids: {
              remove_properties: [propertyId]
            }
          }
        }),
      }),
      /** unregister property */
      unregisterProperty: builder.mutation<any, { userTenantId: string, propertyId: string }>({
        query: ({ userTenantId, propertyId }: { userTenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}/unregister`,
          method: HTTPMethodType.PATCH,
        }),
      }),
      /** delete property */
      deleteProperty: builder.mutation<any, { userTenantId: string, propertyId: string }>({
        query: ({ userTenantId, propertyId }: { userTenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}`,
          method: HTTPMethodType.DELETE,
        }),
      }),
      /** pings gateway/kiosk via mqtt */
      healthcheck: builder.mutation<void, { userTenantId: string, propertyId: string }>({
        query: ({ userTenantId, propertyId }: { userTenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}/health`,
          method: HTTPMethodType.GET,
        })
      }),
      /** cleans up unlinked device entities */
      cleanup: builder.mutation<void, { userTenantId: string, propertyId: string }>({
        query: ({ userTenantId, propertyId }: { userTenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}/cleanup`,
          method: HTTPMethodType.DELETE,
        })
      }),
      /** uploads photo for home */
      uploadHomePhoto: builder.mutation<void, { userTenantId: string, propertyId: string, file: FormData }>({
        query: ({ userTenantId, propertyId, file }: { userTenantId: string, propertyId: string, file: FormData }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}/image`,
          method: HTTPMethodType.PUT,
          body: file,
        })
      }),
      /** uploads kiosk background image */
      uploadKioskBackground: builder.mutation<void, { userTenantId: string, propertyId: string, file: FormData }>({
        query: ({ userTenantId, propertyId, file }: { userTenantId: string, propertyId: string, file: FormData }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}/background`,
          method: HTTPMethodType.PUT,
          body: file,
        })
      }),
      /** removes photo for home */
      deleteHomePhoto: builder.mutation<void, { userTenantId: string, propertyId: string }>({
        query: ({ userTenantId, propertyId }: { userTenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}/image`,
          method: HTTPMethodType.DELETE,
        })
      }),
      /** trigger container update on a single gateway */
      triggerContainerUpdate: builder.mutation<void, { userTenantId: string, propertyId: string }>({
        query: ({ userTenantId, propertyId }: { userTenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}/update/containers`,
          method: HTTPMethodType.POST,
        })
      }),
      /** trigger container update on all gateways */
      triggerAllContainerUpdates: builder.mutation<{ properties_updated: number }, { userTenantId: string }>({
        query: ({ userTenantId }: { userTenantId: string }) => ({
          url: `/tenant/${U(userTenantId)}/update/containers/all`,
          method: HTTPMethodType.POST,
        })
      }),
      /** request a gateway to report its container versions */
      requestVersionReport: builder.mutation<void, { userTenantId: string, propertyId: string }>({
        query: ({ userTenantId, propertyId }: { userTenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}/report/versions`,
          method: HTTPMethodType.POST,
        })
      }),
      /** get container versions for a gateway */
      getContainerVersions: builder.query<ContainerVersions, { userTenantId: string, propertyId: string }>({
        query: ({ userTenantId, propertyId }: { userTenantId: string, propertyId: string }) => ({
          url: `/tenant/${U(userTenantId)}/properties/${U(propertyId)}/versions`,
          method: HTTPMethodType.GET,
        })
      }),
    };
  }
});



export const {
  // endpoints in format:
  //  endpointName => useEndpointNameQuery/useEndpointNameMutation
  useGetPropertyQuery,
  useGetDevicesQuery,
  useGetPropertyManifestQuery,
  useUpdatePropertyMutation,
  useAbandonPropertyMutation,
  useUnregisterPropertyMutation,
  useDeletePropertyMutation,
  useHealthcheckMutation,
  useCleanupMutation,
  useUploadHomePhotoMutation,
  useUploadKioskBackgroundMutation,
  useDeleteHomePhotoMutation,
  useTriggerContainerUpdateMutation,
  useTriggerAllContainerUpdatesMutation,
  useRequestVersionReportMutation,
  useGetContainerVersionsQuery,
} = homeApi;
